import clsx from "clsx";
import { FunctionComponent, useLayoutEffect, useState } from "react";

import {
  credits,
  creditsNote,
} from "@story/data-centers-hub/config/credits";
import {
  HUB_DEPART_GONE_LEAD_MS,
  introWipeMs,
} from "@story/data-centers-hub/config/intro";
import { scheduleEnterFillPins } from "@story/data-centers-hub/config/scheduleEnterFillPins";
import { wipeClipAnimAttrs } from "@story/data-centers-hub/config/wipeClipAnimAttrs";
import { useHubNarrowViewport } from "@story/data-centers-hub/hooks/useHubNarrowViewport";
import Footer from "@voxmedia/duet-ui/components/navigation/Footer";
import vergeDark from "@voxmedia/duet-ui/themes/theverge-dark.css";

import * as styles from "./CreditsSlide.css";

interface Props {
  /** This stop is the active slide. */
  active: boolean;
  /**
   * Play the center wipe. Motion-off paints the block open in document flow.
   */
  animate: boolean;
}

/**
 * Closing slide: story credits, then the standard Verge footer. Motion-on
 * reveals it up and down from the horizontal center. No mosaic cells.
 *
 * PMC privacy banner lives on the hub root (`HubPrivacyBanner`) — not here —
 * so the consent notice is not deferred until this stop.
 */
const CreditsSlide: FunctionComponent<Props> = ({ active, animate }) => {
  const narrow = useHubNarrowViewport();
  /** Frame visible at the closed center clip — must precede `open` by a paint. */
  const [revealed, setRevealed] = useState(!animate || active);
  const [open, setOpen] = useState(!animate || active);
  /** Enter open pin — same class as chapter `data-hub-fill`. */
  const [fillPin, setFillPin] = useState(!animate || active);
  /** Near wipe-end: drop enter animation — same as chapter `data-hub-fill-lock`. */
  const [fillLock, setFillLock] = useState(!animate || active);
  /**
   * Close finished — hide frame paint while `revealed` still holds clip-anim
   * (chapter `departGone`). Dropping `revealed` at wipe-end tore down the
   * close fill and flickered credits→Arizona.
   */
  const [departGone, setDepartGone] = useState(false);

  // Layout — must run before paint. `useEffect` left one frame of
  // active→inactive with `open` still true and no `depart`, so the enter
  // rule replayed (credits→Arizona start flicker).
  useLayoutEffect(() => {
    if (!animate) {
      setRevealed(true);
      setOpen(true);
      setFillPin(true);
      setFillLock(true);
      setDepartGone(false);
      return;
    }
    if (!active) {
      // Match chapter depart: keep the open pin for a beat so fill-lock cannot
      // tear down onto a closed pose on the same frame as `open` flips (that
      // was the credits→Arizona start flicker). Then close.
      setDepartGone(false);
      const wipeMs = introWipeMs(narrow);
      let openRaf = 0;
      let goneTimer = 0;
      let hide = 0;
      const departDebounce = window.setTimeout(() => {
        setFillPin(false);
        setFillLock(false);
        openRaf = requestAnimationFrame(() => {
          openRaf = requestAnimationFrame(() => {
            setOpen(false);
            goneTimer = window.setTimeout(() => {
              setDepartGone(true);
            }, Math.max(0, wipeMs - HUB_DEPART_GONE_LEAD_MS));
            hide = window.setTimeout(() => {
              setRevealed(false);
            }, wipeMs + 48);
          });
        });
      }, 120);
      return () => {
        window.clearTimeout(departDebounce);
        cancelAnimationFrame(openRaf);
        window.clearTimeout(goneTimer);
        window.clearTimeout(hide);
      };
    }
    // Show the frame at inset(50%) first; opening in the same paint as
    // visibility:visible was covering Arizona’s hed before the wipe ran.
    setRevealed(true);
    setDepartGone(false);
    setOpen(false);
    setFillPin(false);
    setFillLock(false);
    const wipeMs = introWipeMs(narrow);
    let cancelFillPins: (() => void) | undefined;
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        setOpen(true);
        cancelFillPins = scheduleEnterFillPins({
          wipeMs,
          setFillPin,
          setFillLock,
        });
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
      cancelFillPins?.();
    };
  }, [active, animate, narrow]);

  // Stay "open" for chrome (`data-credits-open`) through the close wipe —
  // flipping off with `open` on the first depart frame flashed package nav.
  const creditsOpen = animate
    ? active
      ? open
      : revealed && !departGone
    : active;
  // Keep clip-anim for enter and for the whole close (revealed stays until
  // after depart-gone). Idle never-opened credits stay unscoped.
  const creditsClip = wipeClipAnimAttrs({
    animate: animate && revealed,
    active,
    depart: revealed && !active,
    open,
    fillPin,
    fillLock,
    departGone,
  });

  const panel = (
    <div
      className={clsx(
        styles.root,
        animate && styles.wipe,
        open && styles.open,
        !animate && styles.ruled,
      )}
      data-credits-open={creditsOpen ? "" : undefined}
      {...creditsClip}
    >
      <div
        className={clsx(styles.scroll, !animate && styles.scrollStatic)}
        data-hub-nested-scroll=""
      >
        {credits.length > 0 ? (
          <section className={styles.section} aria-label="Credits">
            <div className={styles.inner}>
              <h2 className={styles.header}>Credits</h2>
              <ul className={styles.list}>
                {credits.map((item) => (
                  <li key={item.title} className={styles.item}>
                    <span className={styles.role}>{item.title}:</span>
                    <span className={styles.names}>{item.names}</span>
                  </li>
                ))}
              </ul>
              {creditsNote ? <p className={styles.note}>{creditsNote}</p> : null}
            </div>
          </section>
        ) : null}
        <Footer className={vergeDark} showPmcPrivacyBanner={false} />
      </div>
    </div>
  );

  // Motion-off: the panel is the stacked block, with rules on its own edges.
  // Motion-on: a frame outside the clip carries the rules so they travel
  // with the opening (clip-path would hide borders until the wipe finished).
  if (!animate) {
    return panel;
  }

  // Expose to AT / tab only while this stop is live and open. `open &&
  // !departGone` alone left the footer focusable during the depart debounce
  // (still `open`, already inactive).
  const creditsAccessible = active && open && !departGone;

  return (
    <div
      className={clsx(
        styles.frame,
        revealed && !departGone && styles.frameRevealed,
        departGone && styles.frameGone,
        open && styles.frameOpen,
      )}
      aria-hidden={creditsAccessible ? undefined : true}
      inert={creditsAccessible ? undefined : true}
    >
      {panel}
    </div>
  );
};

export default CreditsSlide;
